import "../../../components/common/Tables/DataTable.css"
import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import RootState from "../../../redux/states/root.state";
import { getFirmwareFile } from "../../../redux/actions/firmware.actions";
import { AppDispatch } from "../../../redux/store";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { BugIcon, DownloadLineIcon, PencilIcon, TrashIcon } from "../../../components/common/Icons/Icons";
import { palName } from "../../../utils/brand";

type Order = 'asc' | 'desc';
const headers = [
  {id:'type', label:'Type'},
  {id:'version', label:'Version'},
  {id:'description', label:'Description'},
  {id:'updated_at', label:'Last Updated'},
  {id:'created_at', label:'Uploaded'},
  {id:'edit', label:'Edit', align:'center'},
  {id:'delete', label:'Delete', align:'center'},
  {id:'download', label:'Download', align:'center'},
];

const unsortable = ['description', 'edit', 'delete', 'download'];

// Row actions pinned to the right edge (px, fits each heading)
const actionWidths: Record<string, number> = {
  edit: 60,
  delete: 68,
  download: 88,
};

const TYPE_LABEL: Record<string, string> = {
  yocto: 'Yocto',
  lpc: 'LPC',
  spaboy: palName,
};

const formatDate = (value: any) =>
  value ? <span className="dt-date">{new Date(value).toLocaleString()}</span> : <span className="dt-na">—</span>;

interface FirmwareTableProps {
  setEditOpen: any;
  setDeleteOpen: any;
  setViewOpen: any;
  setFirmwareToModify: any;
  // Page controls (filters toggle, Upload Firmware) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const FirmwareTable: React.FC<FirmwareTableProps> = ({setEditOpen, setDeleteOpen, setViewOpen, setFirmwareToModify, toolbar}) => {
  const dispatch: AppDispatch = useDispatch();
  const {data:firmwares, meta, loading} = useSelector((state: RootState) => state.firmware);
  const [searchParams, setSearchParams] = useSearchParams();

  // Sorted by the API (read from the URL so the arrow survives a reload)
  const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
  const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');

  // The file link is fetched on demand; remember which row is waiting on it
  const [downloading, setDownloading] = useState<any>(null);

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(header_id);
    searchParams.set("sort_direction", newOrder);
    searchParams.set("sort_by", header_id);
    setSearchParams(searchParams);
  };

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.last_page ?? meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  };

  const openModal = (firmware: any, modal: 'edit' | 'delete' | 'view') => {
    setFirmwareToModify(firmware);
    if (modal === 'edit') setEditOpen(true);
    else if (modal === 'delete') setDeleteOpen(true);
    else setViewOpen(true);
  };

  const downloadFile = async (firmware: { id: any; }) => {
    setDownloading(firmware.id);
    try {
      const data: any = await dispatch(getFirmwareFile({firmware_id: firmware.id}));
      const url = data?.payload?.data?.url;
      if (!url) return;
      const link = document.createElement('a');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  };

  const pinned = headers.map((header) => header.id).filter((id) => id in actionWidths);

  const pinStyle = (id: string) => {
    const index = pinned.indexOf(id);
    if (index < 0) return undefined;
    const right = pinned.slice(index + 1).reduce((sum, next) => sum + actionWidths[next], 0);
    return {'--dt-right': `${right}px`, '--dt-w': `${actionWidths[id]}px`} as React.CSSProperties;
  };

  const cellClass = (header: typeof headers[number]) => [
    header.align ? 'dt-center' : '',
    pinned.includes(header.id) ? 'dt-pin' : '',
    pinned[0] === header.id ? 'dt-pin-start' : '',
  ].filter(Boolean).join(' ') || undefined;

  const renderCell = (id: string, f: any) => {
    switch (id) {
      case 'type':
        return (
          <span className="fw-type">
            <span className="dt-tag">{TYPE_LABEL[f?.type] ?? f?.type ?? '—'}</span>
            {!!f?.is_dev &&
              <Tooltip title="Developer firmware" arrow>
                <span className="fw-dev" aria-label="Developer firmware"><BugIcon/></span>
              </Tooltip>}
          </span>
        );

      case 'version':
        return (
          <span className="fw-version">
            <span className="dt-strong dt-num">{f?.version ?? '—'}</span>
            {!!f?.is_latest && <span className="dt-pill good">Latest</span>}
            {!!f?.is_admin && <span className="dt-pill caution">Admin</span>}
          </span>
        );

      case 'description':
        return f?.description
          ? (
            <Tooltip title="Read the full description" arrow>
              <button type="button" className="dt-title-btn fw-description" onClick={() => openModal(f, 'view')}>
                {f.description}
              </button>
            </Tooltip>
          )
          : <span className="dt-na">—</span>;

      case 'updated_at':
        return formatDate(f?.updated_at);

      case 'created_at':
        return formatDate(f?.created_at);

      case 'edit':
        return (
          <Tooltip title="Edit firmware" arrow>
            <button type="button" className="dt-act" onClick={() => openModal(f, 'edit')} aria-label="Edit firmware">
              <PencilIcon/>
            </button>
          </Tooltip>
        );

      case 'delete':
        return (
          <Tooltip title="Delete firmware" arrow>
            <button type="button" className="dt-act danger" onClick={() => openModal(f, 'delete')} aria-label="Delete firmware">
              <TrashIcon/>
            </button>
          </Tooltip>
        );

      case 'download':
        return f?.firmware_file_path ? (
          <Tooltip title="Download firmware file" arrow>
            <button
              type="button"
              className="dt-act"
              onClick={() => downloadFile(f)}
              disabled={downloading === f.id}
              aria-label="Download firmware file"
            >
              {downloading === f.id
                ? <span className="spinner lp-side-spinner" role="progressbar" aria-label="Preparing download"/>
                : <DownloadLineIcon/>}
            </button>
          </Tooltip>
        ) : <span className="dt-na">—</span>;

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {loading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading firmware"/></div>
        ) : firmwares?.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    style={pinStyle(header.id)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {!unsortable.includes(header.id) ? (
                      <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={() => handleRequestSort(header.id)}/>
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {firmwares.map((f: any, i: number) => (
                <tr key={f?.id ?? i}>
                  {headers.map((header) => (
                    <td key={header.id} className={cellClass(header)} style={pinStyle(header.id)}>
                      {renderCell(header.id, f)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Firmware Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={firmwares?.length ?? 0}
        total={total}
        noun="Firmware"
        page={page}
        totalPages={totalPages}
        loading={loading}
        onPage={goToPage}
      />
    </div>
  );
};

export default FirmwareTable;
